import { Injectable } from '@angular/core';
import { Actions, createEffect, Effect, ofType } from '@ngrx/effects';
import { MarkAsPristineAction, MarkAsUnsubmittedAction, SetValueAction } from 'ngrx-forms';
import { BsModalService } from 'ngx-bootstrap/modal';
import { of } from 'rxjs/index';
import { catchError, concatMap, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { AccountService } from '../../../../core/services/account.service';
import { SystemSettings } from '../../../../core/state/system-settings.state';
import { AdminAccountService } from '../../../../services/admin/admin-account.service';
import { ActionType, selectAction } from '../../../../shared/state/index.state';
import { AdminSystemSettingsActions } from './admin-system-settings-actions.service';
import { SYSTEM_SETTINGS_FORM_ID } from './admin-system-settings.reducer';
import { AdminSystemSettingsSelectors } from './admin-system-settings-selectors.service';

/**
 * https://www.intertech.com/Blog/ngrx-tutorial-actions-reducers-and-effects/
 * https://medium.com/frontend-fun/angular-ngrx-a-clean-and-clear-introduction-4ed61c89c1fc
 */

@Injectable()
export class AdminSystemSettingsEffects {

    constructor(private actions$: Actions,
                private selectors: AdminSystemSettingsSelectors,
                private modalService: BsModalService,
                private accountService: AccountService,
                private adminAccountService: AdminAccountService) {
        // this.listenToRouter();
    }

    @Effect() getSystemSettingsAdmin$ = this.actions$
        .pipe(
            selectAction(AdminSystemSettingsActions.GET_SYSTEM_SETTINGS_FROM_ADMIN),
            switchMap(() =>
                this.adminAccountService.getSystemSettings()
                // this.accountService.getSystemSettings()
                    .pipe(
                        map((settings: SystemSettings) =>
                            AdminSystemSettingsActions.GET_SYSTEM_SETTINGS_SUCCESS_FROM_ADMIN(settings)
                        ),

                        catchError(error =>
                            of(AdminSystemSettingsActions.GET_SYSTEM_SETTINGS_FAIL({error}))
                        )
                    )
            )
        );

    /**
     * Side effect saving system setttings from edited form (from the state store).
     *
     * NB: Using concatMap to ensure that the API request does not get cancelled by another save action
     * TODO: Let reducer handle state as well and toggle loading state
     */
    @Effect() saveSystemSettings$ = this.actions$
        .pipe(
            selectAction(AdminSystemSettingsActions.SAVE_SYSTEM_SETTINGS),
            withLatestFrom(this.selectors.selectFormStateValue()),
            map(([action, formValue]) => {
                const settings = [
                    formValue.settings
                ];

                return settings;
            }),

            concatMap(latest => // Use concatMap to ensure the API request completes and not cancelled by more save actions
                this.adminAccountService.saveSystemSettings(latest[0])
                    .pipe(
                        switchMap(settings => {
                            return [
                                AdminSystemSettingsActions.SAVE_SYSTEM_SETTINGS_SUCCESS(settings),
                                new MarkAsPristineAction(SYSTEM_SETTINGS_FORM_ID),
                                new MarkAsUnsubmittedAction(SYSTEM_SETTINGS_FORM_ID)
                            ];
                        }),

                        // We place catchError handlers inside a pipe of the API call
                        // This way, the new (handled) Observable only replaces the failed one
                        // inside the switch, rather than the whole Effect Observable.
                        // https://medium.com/city-pantry/handling-errors-in-ngrx-effects-a95d918490d9
                        catchError(error => {
                            let errorObject = error;

                            if (error.error !== undefined) {
                                errorObject = error.error;

                                // if (error.error.error !== undefined) {
                                //    errorObject = error.error.error;
                                // }
                            }

                            return of(AdminSystemSettingsActions.SAVE_SYSTEM_SETTINGS_FAIL(errorObject));
                        })
                    )
            )
        );
}
